import { ref } from "vue"
import { toast } from "vue-sonner"
import { type AssetView, assetView } from "@auger/core/fields"
import { type UploadTarget, useUploadsStore } from "@/stores/uploads"

/**
 * Drag-and-drop file uploads onto a surface. Tracks whether files are being
 * dragged over it, ignoring internal drags (cards, columns), and hands dropped
 * or picked files to the app's upload queue (stores/uploads), which the upload
 * tray shows.
 *
 * @example
 * const { isDragOver, handleDrop, ...handlers } = useUploader(() => ({
 *   record: props.record,
 *   destination: "Board materials",
 *   onUploaded: (asset) => attach(asset),
 * }))
 */
export function useUploader(
  target: () => UploadTarget = () => ({}),
  /** The one kind of file this surface takes; others are refused with a toast. */
  accepts: () => AssetView | undefined = () => undefined
) {
  const store = useUploadsStore()

  const isDragOver = ref(false)
  let dragCounter = 0

  // Files from outside the browser carry "Files"; internal drags don't.
  function isFileDrag(event: DragEvent) {
    return !!event.dataTransfer?.types?.includes("Files")
  }

  function handleDragEnter(event: DragEvent) {
    if (!isFileDrag(event)) return
    dragCounter++
    if (dragCounter === 1) isDragOver.value = true
  }

  function handleDragLeave(event: DragEvent) {
    if (!isFileDrag(event)) return
    dragCounter = Math.max(dragCounter - 1, 0)
    if (dragCounter === 0) isDragOver.value = false
  }

  function handleDragOver(event: DragEvent) {
    if (!isFileDrag(event)) return
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy"
  }

  function handleDrop(event: DragEvent) {
    if (!isFileDrag(event)) return
    dragCounter = 0
    isDragOver.value = false
    processFiles(Array.from(event.dataTransfer?.files || []))
  }

  /** Queue files for upload: dropped ones, or from a file input. */
  function processFiles(files: File[], override: UploadTarget = {}) {
    const kind = accepts()
    const taken = kind ? files.filter((f) => assetView(f.type) === kind) : files
    const refused = files.filter((f) => !taken.includes(f))
    if (refused.length) {
      toast.error(`Only ${kind} files go here — skipped ${refused.map((f) => f.name).join(", ")}`)
    }
    if (!taken.length) return []
    return store.enqueue(taken, { ...target(), ...override })
  }

  /** Reset the drag state without uploading (the drop landed elsewhere). */
  function cancelDrag() {
    dragCounter = 0
    isDragOver.value = false
  }

  return {
    isDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    processFiles,
    cancelDrag,
  }
}
